/**
 * The review gate: everything a human needs before a machine's plan is allowed
 * to become an order.
 *
 * The design rule this file follows is that nothing here may make the proposal
 * look better than the evaluation says it is.
 *
 *   - A factor with no measurement renders as UNKNOWN with the words "NO
 *     MEASUREMENT" where the number would be. It never gets the tick, and it
 *     never gets the OK colour. A gate that looks greener the less it knows is
 *     the most dangerous shape a gate can have, and that failure is a styling
 *     decision as much as a logic one.
 *   - The narrative is attributed. It is the model's paragraph about its own
 *     plan, rendered inside a labelled region so it cannot be read as a system
 *     finding. Nothing in this component parses it.
 *   - The tool trace is on the panel, not in a log. "Which tool produced this
 *     number" is the reviewer's second question and it should not require
 *     leaving the screen.
 *   - A NO-GO cannot be approved by clicking harder. It needs an explicit
 *     override tick AND a typed reason, because the reason is the artifact that
 *     survives the shift.
 *   - A real aircraft needs its callsigns typed. A click is a thing hands do by
 *     accident; typing "SPECTRE 1, SPECTRE 2" is not.
 */

import { useMemo, useState } from 'react';

import type {
  CopilotStep,
  EvaluationFactor,
  FactorSeverity,
  ProposalVerdict,
  StoredProposal,
} from '@/api/copilot';
import { copilotActions, useCopilotStore } from '@/stores/copilotStore';
import { useDroneStore } from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
/** UNKNOWN gets its own tone. Not green, not grey-as-in-fine — a cold blue that
 *  reads as "we did not measure this", which is what it means. */
const UNKNOWN = 'oklch(0.72 0.09 240)';

const SEVERITY_TONE: Record<FactorSeverity, string> = {
  ok: OK,
  unknown: UNKNOWN,
  caution: WARN,
  'no-go': DANGER,
};

/** No tick for UNKNOWN. A checkmark on an unmeasured factor is the exact lie
 *  this panel is built to avoid. */
const SEVERITY_MARK: Record<FactorSeverity, string> = {
  ok: '✓',
  unknown: '?',
  caution: '!',
  'no-go': '✕',
};

const SEVERITY_WORD: Record<FactorSeverity, string> = {
  ok: 'OK',
  unknown: 'UNKNOWN',
  caution: 'CAUTION',
  'no-go': 'NO-GO',
};

const VERDICT_TONE: Record<ProposalVerdict, string> = {
  go: OK,
  caution: WARN,
  'no-go': DANGER,
};

const VERDICT_WORD: Record<ProposalVerdict, string> = {
  go: 'GO',
  caution: 'CAUTION',
  'no-go': 'NO-GO',
};

/** The measurement behind a factor, or the plain statement that there is none. */
export function formatMeasurement(factor: EvaluationFactor): string {
  if (factor.value === null) return 'NO MEASUREMENT';
  const rounded = Math.abs(factor.value) >= 100 ? factor.value.toFixed(0) : factor.value.toFixed(1);
  return factor.unit ? `${rounded} ${factor.unit}` : rounded;
}

function formatDistance(m: number): string {
  return m < 1000 ? `${m.toFixed(0)} m` : `${(m / 1000).toFixed(2)} km`;
}

/**
 * What the operator is asked to type for a real-fleet release.
 *
 * The roster is passed in rather than read from the store here, so this and the
 * per-drone route rows below resolve names from the same snapshot. Reading the
 * store separately in two places lets the panel display one callsign and demand
 * a different one after a drone reconnects, which reads as "the confirmation is
 * broken" rather than "the roster changed".
 */
export function callsignsFor(
  proposal: StoredProposal,
  roster: Map<DroneId, { callsign: string }>,
): string[] {
  return Object.keys(proposal.proposal.routes).map(
    (droneId) => roster.get(droneId as DroneId)?.callsign ?? droneId,
  );
}

function normalize(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, ' ');
}

/** Order- and case-insensitive, but every callsign must be there and no extras.
 *  Typing one of two aircraft is not a confirmation of a two-ship. */
export function callsignsMatch(typed: string, expected: string[]): boolean {
  const given = typed
    .split(/[,\n]/)
    .map(normalize)
    .filter((part) => part.length > 0);
  const want = expected.map(normalize);
  if (given.length !== want.length) return false;
  const remaining = [...want];
  for (const one of given) {
    const at = remaining.indexOf(one);
    if (at === -1) return false;
    remaining.splice(at, 1);
  }
  return remaining.length === 0;
}

function Chip({ text, tone }: { text: string; tone: string }) {
  return (
    <span
      className="rounded-full px-2 py-[2px] font-mono text-[8px] font-bold tracking-wide"
      style={{ color: tone, background: `color-mix(in oklch, ${tone} 16%, transparent)` }}
    >
      {text}
    </span>
  );
}

function FactorRow({ factor }: { factor: EvaluationFactor }) {
  const tone = SEVERITY_TONE[factor.severity];
  const unmeasured = factor.severity === 'unknown';
  return (
    <li
      className="flex flex-col gap-0.5 rounded-[10px] border px-2.5 py-1.5"
      style={{
        borderColor: `color-mix(in oklch, ${tone} ${unmeasured ? 55 : 32}%, transparent)`,
        background: `color-mix(in oklch, ${tone} 7%, transparent)`,
        // A dashed edge on an unmeasured factor, so the shape of the row says
        // "incomplete" before anyone reads the word.
        borderStyle: unmeasured ? 'dashed' : 'solid',
      }}
    >
      <div className="flex items-center gap-2">
        <span className="w-3 shrink-0 text-center font-mono text-[10px]" style={{ color: tone }}>
          {SEVERITY_MARK[factor.severity]}
        </span>
        <span className="font-mono text-[10.5px] text-white/85">{factor.name}</span>
        {factor.droneId ? (
          <span className="label-mono text-[8px] text-white/40">{factor.droneId}</span>
        ) : null}
        <span className="ml-auto label-mono text-[8px]" style={{ color: tone }}>
          {SEVERITY_WORD[factor.severity]}
        </span>
      </div>
      <div className="flex items-baseline gap-2 pl-5">
        <span className="text-[10px] leading-snug text-white/55">{factor.detail}</span>
        <span
          className="tnum ml-auto shrink-0 text-[10.5px]"
          style={{ color: factor.value === null ? UNKNOWN : 'rgba(244,244,245,0.85)' }}
        >
          {formatMeasurement(factor)}
        </span>
      </div>
    </li>
  );
}

export interface ProposalReviewProps {
  proposal: StoredProposal;
  /** The tools the turn ran to build this. Provenance, shown on the panel. */
  steps?: CopilotStep[];
  /** Hand the proposal back to the conversation for a revision. */
  onEdit: (proposal: StoredProposal) => void;
}

/**
 * How many factors of a kind, or null when the server did not send the list.
 *
 * Null and zero are different answers and this screen must not confuse them:
 * zero means the plan has nothing blocking it, null means nobody knows whether
 * it does. Rendering the second as the first is how a NO-GO gets approved.
 */
export function factorCount(factors: unknown): number | null {
  return Array.isArray(factors) ? factors.length : null;
}

export function ProposalReview({ proposal, steps = [], onEdit }: ProposalReviewProps) {
  const { evaluation, proposal: mission } = proposal;
  const isReal = proposal.fleet === 'real';
  const isNoGo = evaluation.verdict === 'no-go';

  const [override, setOverride] = useState(false);
  const [note, setNote] = useState('');
  const [typedCallsigns, setTypedCallsigns] = useState('');
  /** Which decision the operator has armed but not yet confirmed. Real aircraft
   *  never skip this step. */
  const [pending, setPending] = useState<'approve' | 'execute' | null>(null);
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);

  const roster = useDroneStore((state) => state.drones);
  const expected = useMemo(() => callsignsFor(proposal, roster), [proposal, roster]);
  const callsignsOk = callsignsMatch(typedCallsigns, expected);

  // A NO-GO needs the tick AND the reason. The tick alone records that somebody
  // clicked; the sentence records why, which is the part anybody reads later.
  const overrideSatisfied = !isNoGo || (override && note.trim().length > 0);
  const canApprove = overrideSatisfied && proposal.status === 'proposed';

  const droneIds = Object.keys(mission.routes);

  const run = (
    action: () => Promise<{ ok: boolean; message: string }>,
  ): void => {
    // Every decision acts on the store's active proposal, but this panel
    // renders from its prop. In the app they are always the same object; a
    // second call site passing a different one would let an operator read one
    // mission and approve another, which for a review gate is the whole
    // failure. Refuse rather than decide against something unseen.
    const active = useCopilotStore.getState().activeProposal;
    if (!active || active.proposalId !== proposal.proposalId) {
      setNotice({
        text: 'This review is out of step with the open proposal. Reopen it before deciding.',
        warn: true,
      });
      return;
    }
    setNotice(null);
    void action().then((result) => {
      setNotice({ text: result.message, warn: !result.ok });
      if (result.ok) {
        setPending(null);
        setTypedCallsigns('');
      }
    });
  };

  const armOrRun = (kind: 'approve' | 'execute') => {
    if (isReal) {
      setPending(kind);
      return;
    }
    if (kind === 'approve') run(() => copilotActions.approve(note.trim() || undefined, override));
    else run(() => copilotActions.execute());
  };

  return (
    <section
      aria-label="Proposed mission review"
      className="flex flex-col gap-2.5 rounded-[14px] border border-white/10 bg-black/25 p-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="label-mono text-[9px]">PROPOSED MISSION</span>
        <span className="label-mono text-[8px] text-white/40">REV {proposal.revision}</span>
        <Chip text={isReal ? 'REAL AIRCRAFT' : 'SIMULATED'} tone={isReal ? DANGER : WARN} />
        <Chip text={proposal.status.toUpperCase()} tone="rgba(244,244,245,0.6)" />
      </div>

      {/* The verdict, first and largest. Everything below it is the evidence. */}
      <div
        className="flex flex-col gap-1 rounded-[12px] border px-3 py-2.5"
        style={{
          borderColor: `color-mix(in oklch, ${VERDICT_TONE[evaluation.verdict]} 55%, transparent)`,
          background: `color-mix(in oklch, ${VERDICT_TONE[evaluation.verdict]} 10%, transparent)`,
        }}
      >
        <div className="flex items-center gap-2">
          <span
            aria-label={`Verdict ${VERDICT_WORD[evaluation.verdict]}`}
            className="font-mono text-[18px] font-bold tracking-wide"
            style={{ color: VERDICT_TONE[evaluation.verdict] }}
          >
            {VERDICT_WORD[evaluation.verdict]}
          </span>
          {/* Counted from the arrays when they are there, and SAID to be
              missing when they are not — never defaulted to zero.

              This crashed the whole review panel once: the server was not
              serialising either list, `.length` threw on undefined, and the one
              screen that gates every approval went blank on the one event it
              exists for. The obvious repair is `?.length ?? 0`, and it is the
              wrong one: a plan whose blocking factors failed to arrive would
              render "0 BLOCKING" and read as cleared. A missing count is not a
              count of zero, and on this screen the difference is whether
              somebody approves a NO-GO. */}
          {factorCount(evaluation.unknownFactors) === null ? (
            <Chip text="FACTOR COUNTS UNAVAILABLE" tone={DANGER} />
          ) : (
            <>
              {factorCount(evaluation.unknownFactors) ? (
                <Chip text={`${factorCount(evaluation.unknownFactors)} UNKNOWN`} tone={UNKNOWN} />
              ) : null}
              {factorCount(evaluation.blockingFactors) ? (
                <Chip
                  text={`${factorCount(evaluation.blockingFactors)} BLOCKING`}
                  tone={DANGER}
                />
              ) : null}
            </>
          )}
        </div>
        <p className="text-[11px] leading-relaxed text-white/70">{evaluation.summary}</p>
        {evaluation.governingFactor ? (
          <p className="text-[10px] leading-snug text-white/50">
            <span className="label-mono text-[8px] text-white/40">GOVERNING · </span>
            {evaluation.governingFactor.name} — {evaluation.governingFactor.detail} (
            {formatMeasurement(evaluation.governingFactor)})
          </p>
        ) : null}
      </div>

      {evaluation.unknownFactors.length ? (
        <p
          className="rounded-[10px] border border-dashed px-2.5 py-1.5 text-[10px] leading-relaxed"
          style={{ borderColor: `color-mix(in oklch, ${UNKNOWN} 55%, transparent)`, color: UNKNOWN }}
        >
          {evaluation.unknownFactors.length} factor
          {evaluation.unknownFactors.length === 1 ? ' was' : 's were'} not measured:{' '}
          {evaluation.unknownFactors.map((f) => f.name).join(', ')}. Missing input is not a pass.
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="label-mono text-[8px] text-white/40">FACTORS</span>
        <ul className="flex flex-col gap-1">
          {evaluation.factors.map((factor, index) => (
            <FactorRow key={`${factor.name}-${factor.droneId ?? ''}-${index}`} factor={factor} />
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-1">
        <span className="label-mono text-[8px] text-white/40">ROUTES</span>
        {droneIds.length === 0 ? (
          <p className="text-[10px] text-white/45">This proposal contains no routes.</p>
        ) : null}
        {droneIds.map((droneId) => {
          const waypoints = mission.routes[droneId] ?? [];
          const lengthM = evaluation.routeLengthM[droneId];
          const violations = evaluation.zoneViolations[droneId] ?? [];
          const callsign = roster.get(droneId as DroneId)?.callsign ?? droneId;
          return (
            <div key={droneId} className="well flex flex-col gap-1 rounded-[11px] px-2.5 py-2">
              <div className="flex items-baseline gap-2">
                <span className="tnum text-[11.5px]">{callsign}</span>
                <span className="label-mono text-[8px] text-white/40">{droneId}</span>
                <span className="tnum ml-auto text-[10.5px] text-white/65">
                  {waypoints.length} WP ·{' '}
                  {lengthM === undefined ? 'length not computed' : formatDistance(lengthM)}
                </span>
              </div>
              {mission.roles[droneId] ? (
                <p className="text-[10px] leading-snug text-white/50">{mission.roles[droneId]}</p>
              ) : null}
              {violations.length ? (
                <ul className="flex flex-col gap-0.5">
                  {violations.map((violation, index) => (
                    <li key={index} className="text-[10px] leading-snug" style={{ color: DANGER }}>
                      ✕ {violation.kind} breach · {violation.zoneName} ·{' '}
                      {violation.onLeg ? `leg ${violation.index + 1}` : `waypoint ${violation.index + 1}`}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Attributed, and inside its own labelled region. This paragraph argues
          for the plan; it is not evidence about the plan. */}
      <section
        aria-label="AI narrative — the model's own words"
        className="flex flex-col gap-1 rounded-[11px] border border-dashed border-white/15 px-2.5 py-2"
      >
        <span className="label-mono text-[8px] text-white/40">
          AI NARRATIVE — THE MODEL&apos;S OWN WORDS
        </span>
        <p className="whitespace-pre-wrap text-[10.5px] leading-relaxed text-white/70">
          {mission.narrative || 'The model gave no narrative for this proposal.'}
        </p>
        <span className="text-[9px] leading-snug text-white/35">
          Display only. Nothing above was measured from this text, and nothing here is a system
          finding.
        </span>
      </section>

      {steps.length ? (
        <div className="flex flex-col gap-1">
          <span className="label-mono text-[8px] text-white/40">
            RATIONALE — WHICH TOOL PRODUCED WHAT
          </span>
          <ol className="flex flex-col gap-1">
            {steps.map((step, index) => (
              <li key={index} className="well flex flex-col gap-0.5 rounded-[10px] px-2.5 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="tnum text-[9px] text-white/35">{index + 1}</span>
                  <span className="font-mono text-[10px] text-white/80">{step.tool}</span>
                  <span
                    className="ml-auto label-mono text-[8px]"
                    style={{ color: step.ok ? OK : DANGER }}
                  >
                    {step.ok ? 'OK' : 'FAILED'}
                  </span>
                </div>
                {step.thought ? (
                  <p className="text-[9.5px] leading-snug text-white/45">{step.thought}</p>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {isNoGo ? (
        <div
          className="flex flex-col gap-1.5 rounded-[11px] border px-2.5 py-2"
          style={{ borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
        >
          <label className="flex items-center gap-2 text-[10.5px]" style={{ color: DANGER }}>
            <input
              type="checkbox"
              checked={override}
              onChange={(event) => setOverride(event.target.checked)}
            />
            Override the NO-GO and approve anyway
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-mono text-[8px] text-white/40">
              WHY — REQUIRED, RECORDED AGAINST YOUR NAME
            </span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={!override}
              rows={2}
              aria-label="Override reason"
              className="well rounded-[8px] px-2 py-1.5 text-[10.5px] outline-none disabled:opacity-40"
            />
          </label>
        </div>
      ) : null}

      {pending !== null ? (
        <div
          className="flex flex-col gap-1.5 rounded-[11px] border px-2.5 py-2"
          style={{ borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
        >
          <span className="label-mono text-[8.5px]" style={{ color: DANGER }}>
            CONFIRM {pending === 'approve' ? 'APPROVAL' : 'RELEASE'} — REAL AIRCRAFT
          </span>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] leading-snug text-white/55">
              Type every callsign to confirm: {expected.join(', ')}
            </span>
            <input
              value={typedCallsigns}
              onChange={(event) => setTypedCallsigns(event.target.value)}
              aria-label="Type the callsigns to confirm"
              placeholder={expected.join(', ')}
              className="well h-8 rounded-[99px] px-3 font-mono text-[11px] outline-none"
            />
          </label>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={!callsignsOk || (pending === 'approve' && !canApprove)}
              onClick={() =>
                pending === 'approve'
                  ? run(() => copilotActions.approve(note.trim() || undefined, override))
                  : run(() => copilotActions.execute(expected))
              }
              className="rounded-full border px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide disabled:opacity-40"
              style={{
                color: DANGER,
                borderColor: `color-mix(in oklch, ${DANGER} 55%, transparent)`,
              }}
            >
              {pending === 'approve' ? 'CONFIRM APPROVE' : 'CONFIRM EXECUTE'}
            </button>
            <button
              type="button"
              onClick={() => {
                setPending(null);
                setTypedCallsigns('');
              }}
              className="rounded-full border border-white/15 px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide text-white/60"
            >
              CANCEL
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {proposal.status === 'proposed' ? (
          <button
            type="button"
            disabled={!canApprove}
            onClick={() => armOrRun('approve')}
            className="rounded-full border px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d', borderColor: 'rgba(255,255,255,0.9)' }}
          >
            APPROVE
          </button>
        ) : null}
        {proposal.status === 'approved' ? (
          <button
            type="button"
            onClick={() => armOrRun('execute')}
            className="rounded-full border px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide"
            style={{ background: 'rgba(255,255,255,0.9)', color: '#0c0c0d', borderColor: 'rgba(255,255,255,0.9)' }}
          >
            EXECUTE
          </button>
        ) : null}
        {proposal.status === 'proposed' || proposal.status === 'approved' ? (
          <button
            type="button"
            onClick={() => run(() => copilotActions.reject(note.trim() || undefined))}
            className="rounded-full border px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide"
            style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
          >
            REJECT
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => onEdit(proposal)}
          className="rounded-full border border-white/15 px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide text-white/70"
        >
          EDIT
        </button>
        {proposal.status === 'executed' && proposal.hasRollback ? (
          <button
            type="button"
            onClick={() => run(() => copilotActions.undo())}
            className="rounded-full border border-white/15 px-3 py-[7px] font-mono text-[9px] font-bold tracking-wide text-white/70"
          >
            UNDO
          </button>
        ) : null}
      </div>

      {isNoGo && !canApprove && proposal.status === 'proposed' ? (
        <p className="text-[9.5px] leading-snug" style={{ color: DANGER }}>
          A NO-GO cannot be approved. Tick the override and type why, or send it back for a revision.
        </p>
      ) : null}

      {notice ? (
        <p className="font-mono text-[10px]" style={{ color: notice.warn ? WARN : OK }}>
          {notice.text}
        </p>
      ) : null}

      {proposal.decisionNote ? (
        <p className="text-[9.5px] leading-snug text-white/45">
          {proposal.decidedBy ?? 'unknown'}: {proposal.decisionNote}
        </p>
      ) : null}
    </section>
  );
}

export default ProposalReview;
